import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as ApproveIcon,
  Cancel as DeclineIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Instagram,
  Email as EmailIcon,
  Sms as SmsIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import SocialMediaApprovals from "./socialMedia";
import { getAuthTokenPayload } from "../../Utils/index";

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const Approvals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Add state for pending count and approved today count
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedTodayCount, setApprovedTodayCount] = useState(0);

  // Get user permissions from JWT auth token menuList
  useEffect(() => {
    try {
      const menuList = getAuthTokenPayload()?.menuList ?? [];
      const menu = menuList.find((m) => m.menuId === "MENU-006");

      setUserPermissions({
        read: menu?.readAccess === "yes",
        write: menu?.writeAccess === "yes",
      });
    } catch (error) {
      console.error("Error reading permissions:", error);
      setUserPermissions({ read: false, write: false });
    }
  }, []);

  // Callback to receive stats data from SocialMediaApprovals component
  const handleStatsUpdate = (stats) => {
    setPendingCount(stats.pendingCount || 0);
    setApprovedTodayCount(stats.approvedTodayCount || 0);
  };

  // Only Social Media tab data for Phase 1
  const tabData = [
    {
      label: "Social Media Posts",
      data: [], // Will be handled by SocialMediaApprovals component
      setData: () => {},
      icon: <Instagram sx={{ fontSize: 20 }} />,
      pendingCount: pendingCount, // Use the actual pending count
    },
    // Phase 2: SMS and Email tabs will be added later
    // {
    //   label: "SMS Campaigns",
    //   data: smsApprovals,
    //   setData: setSmsApprovals,
    //   icon: <SmsIcon sx={{ fontSize: 20 }} />,
    //   pendingCount: smsApprovals.filter((item) => item.status === "Pending").length,
    // },
    // {
    //   label: "Email Campaigns",
    //   data: emailApprovals,
    //   setData: setEmailApprovals,
    //   icon: <EmailIcon sx={{ fontSize: 20 }} />,
    //   pendingCount: emailApprovals.filter((item) => item.status === "Pending").length,
    // },
  ];

  const getCurrentData = () => {
    // Since we only have Social Media tab now, always return empty array
    // Data is handled inside SocialMediaApprovals component
    return [];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return {
          bgcolor: "#fef3c7",
          color: "#d97706",
          border: "1px solid #fbbf24",
        };
      case "Approved":
        return {
          bgcolor: "#dcfce7",
          color: "#059669",
          border: "1px solid #22c55e",
        };
      case "Declined":
        return {
          bgcolor: "#fee2e2",
          color: "#dc2626",
          border: "1px solid #ef4444",
        };
      default:
        return {};
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return { color: "#dc2626", bgcolor: alpha("#dc2626", 0.1) };
      case "Medium":
        return { color: "#d97706", bgcolor: alpha("#d97706", 0.1) };
      case "Low":
        return { color: "#059669", bgcolor: alpha("#059669", 0.1) };
      default:
        return {};
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleMenuOpen = (event, item) => {
    event.stopPropagation(); // Prevent event bubbling
    setAnchorEl(event.currentTarget); // Use currentTarget instead of target
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePreviewOpen = () => {
    setPreviewDialog(true);
    handleMenuClose();
  };

  const handlePreviewClose = () => {
    setPreviewDialog(false);
  };

  const handleActionOpen = (type) => {
    // For approve action, check time validity first
    if (type === "approve" && selectedItem?.scheduleDateTime) {
      if (!isApprovalTimeValid(selectedItem.scheduleDateTime)) {
        const validationMessage = getTimeValidationMessage(
          selectedItem.scheduleDateTime
        );
        alert(validationMessage || "Cannot approve - insufficient time buffer");
        handleMenuClose();
        return;
      }
    }

    setActionType(type);
    setActionDialog(true);
    handleMenuClose();
  };

  const handleActionClose = () => {
    setActionDialog(false);
    setIsSubmitting(false);
  };

  const updateItemStatus = (itemId, newStatus) => {
    // For Phase 1, this will be handled in SocialMediaApprovals component
    setRefreshKey((prev) => prev + 1);
  };

  // ADDED: Helper function to check if scheduled time is valid for approval (at least 10 minutes in future)
  const isApprovalTimeValid = (scheduleDateTime) => {
    if (!scheduleDateTime) return false;

    const now = dayjs().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const scheduledTime = dayjs(scheduleDateTime).tz(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const minValidTime = now.add(10, "minute");

    return scheduledTime.isAfter(minValidTime);
  };

  // ADDED: Get time difference message
  const getTimeValidationMessage = (scheduleDateTime) => {
    if (!scheduleDateTime) return "";

    const now = dayjs().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const scheduledTime = dayjs(scheduleDateTime).tz(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const diffMinutes = scheduledTime.diff(now, "minute");

    if (diffMinutes < 0) {
      return "This post is scheduled in the past and cannot be approved.";
    } else if (diffMinutes < 10) {
      return `This post is scheduled in ${diffMinutes} minute(s). Minimum 10 minutes buffer required.`;
    }
    return "";
  };

  // UPDATED: Handle approve with time validation
  const handleApprove = () => {
    // Validate scheduled time before approving
    if (!isApprovalTimeValid(selectedItem?.scheduleDateTime)) {
      const validationMessage = getTimeValidationMessage(
        selectedItem?.scheduleDateTime
      );
      alert(validationMessage || "Cannot approve - insufficient time buffer");
      handleActionClose();
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      updateItemStatus(selectedItem.id, "Approved");
      setIsSubmitting(false);
      handleActionClose();
    }, 1500);
  };

  const handleDecline = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      updateItemStatus(selectedItem.id, "Declined");
      setIsSubmitting(false);
      handleActionClose();
    }, 1500);
  };

  const currentData = getCurrentData();
  const totalPending = pendingCount; // Use the actual pending count

  // Show permission denied if user has no access
  if (!userPermissions.read && !userPermissions.write) {
    return (
      <Box sx={{ py: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Access Denied
            </Typography>
            <Typography>
              You don't have permission to access the Approvals module. Please
              contact your administrator to request access.
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{ py: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}
      key={refreshKey}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 },
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}
              >
                Social Media Approvals
              </Typography>
              <Typography 
                variant={isMobile ? "body2" : "body1"} 
                sx={{ color: "#64748b" }}
              >
                Review and approve social media posts before they go live
              </Typography>
            </Box>

            {/* Stats cards with actual data */}
            <Box sx={{ display: "flex", gap: 2, width: { xs: "100%", md: "auto" } }}>
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  minWidth: { xs: "100%", sm: 120 },
                  textAlign: "center",
                  border: "2px solid #fbbf24",
                  bgcolor: "#fef3c7",
                  flex: { xs: 1, sm: "none" },
                }}
              >
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ color: "#d97706", fontWeight: 700 }}
                >
                  {totalPending}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#92400e", fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Pending Review
                </Typography>
              </Paper>
            </Box>
          </Box>

          <Alert
            severity={userPermissions.write ? "success" : "info"}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {userPermissions.write ? "Full Access" : "View Only Access"}
            </Typography>
            <Typography variant="body2">
              {userPermissions.write
                ? "You can view, approve, and decline social media posts."
                : "You can view social media posts but cannot approve or decline them."}
            </Typography>
          </Alert>
        </Box>

        {/* Search and Filter Section */}
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by content, hashtags, or platform..."
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#94a3b8", mr: 1 }} />
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                {/* <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  sx={{
                    borderColor: "#e2e8f0",
                    color: "#64748b",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      bgcolor: alpha("#3b82f6", 0.04),
                    },
                  }}
                >
                  Filter
                </Button> */}
                {/* <Button
                  variant="outlined"
                  startIcon={<TrendingUpIcon />}
                  sx={{
                    borderColor: "#e2e8f0",
                    color: "#64748b",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      bgcolor: alpha("#3b82f6", 0.04),
                    },
                  }}
                >
                  Export
                </Button> */}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Social Media Approvals Card */}
        <Card
          sx={{
            borderRadius: 2,
            overflow: "visible", // CHANGED: from "hidden" to "visible" to allow menu to overflow
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <SocialMediaApprovals
              searchTerm={searchTerm}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              userPermissions={userPermissions}
              onPreviewOpen={handlePreviewOpen}
              onActionOpen={handleActionOpen}
              onMenuOpen={handleMenuOpen}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              getStatusColor={getStatusColor}
              onClearSearch={() => setSearchTerm("")}
              onStatsUpdate={handleStatsUpdate}
              isApprovalTimeValid={isApprovalTimeValid}
              getTimeValidationMessage={getTimeValidationMessage}
            />
          </CardContent>
        </Card>

        {/* FIXED: Context Menu with proper positioning */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                borderRadius: 2,
                minWidth: 180,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                mt: 0.5, // Small margin from anchor
              },
            },
          }}
        >
          <MenuItem onClick={handlePreviewOpen}>
            <ViewIcon sx={{ mr: 2, fontSize: 20 }} />
            Preview
          </MenuItem>
          {userPermissions.write &&
            selectedItem?.status === "Pending" &&
            isApprovalTimeValid(selectedItem?.rawScheduleDateTime) && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => handleActionOpen("approve")}
                  sx={{ color: "#059669" }}
                >
                  <ApproveIcon sx={{ mr: 2, fontSize: 20 }} />
                  Approve
                </MenuItem>
                <MenuItem
                  onClick={() => handleActionOpen("decline")}
                  sx={{ color: "#dc2626" }}
                >
                  <DeclineIcon sx={{ mr: 2, fontSize: 20 }} />
                  Decline
                </MenuItem>
              </>
            )}
          {/* Show why approval is disabled */}
          {userPermissions.write &&
            selectedItem?.status === "Pending" &&
            !isApprovalTimeValid(selectedItem?.rawScheduleDateTime) && (
              <>
                <Divider />
                <MenuItem disabled sx={{ opacity: 0.6 }}>
                  <Tooltip
                    title={getTimeValidationMessage(
                      selectedItem?.rawScheduleDateTime
                    )}
                    placement="left"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <TimeIcon
                        sx={{ mr: 2, fontSize: 20, color: "#dc2626" }}
                      />
                      <Typography variant="body2" sx={{ color: "#dc2626" }}>
                        Cannot Approve
                      </Typography>
                    </Box>
                  </Tooltip>
                </MenuItem>
                <MenuItem
                  onClick={() => handleActionOpen("decline")}
                  sx={{ color: "#dc2626" }}
                >
                  <DeclineIcon sx={{ mr: 2, fontSize: 20 }} />
                  Decline
                </MenuItem>
              </>
            )}
        </Menu>

        {/* UPDATED: Preview Dialog with time validation - hide approve button if time expired */}
        <Dialog
          open={previewDialog}
          onClose={handlePreviewClose}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          sx={{ "& .MuiDialog-paper": { borderRadius: isMobile ? 0 : 2 } }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Content Preview
            </Typography>
            <IconButton onClick={handlePreviewClose} size="small">
              <Box component="span" sx={{ fontSize: 24 }}>
                ×
              </Box>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedItem && (
              <Box sx={{ mb: 2 }}>
                {/* UPDATED: Show time validation warning in preview */}
                {selectedItem.status === "Pending" &&
                  selectedItem.rawScheduleDateTime &&
                  !isApprovalTimeValid(selectedItem.rawScheduleDateTime) && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ⚠️ Cannot Approve - Time Buffer Insufficient
                      </Typography>
                      <Typography variant="body2">
                        {getTimeValidationMessage(
                          selectedItem.rawScheduleDateTime
                        )}
                      </Typography>
                    </Alert>
                  )}

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedItem.subject || "Social Media Post"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                >
                  {selectedItem.content ||
                    selectedItem.message ||
                    selectedItem.subject}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Requested by
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedItem.requestedBy}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled for
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedItem.scheduledFor}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handlePreviewClose} sx={{ borderRadius: 2 }}>
              Close
            </Button>
            {/* UPDATED: Only show approve button if time is valid */}
            {userPermissions.write &&
              selectedItem?.status === "Pending" &&
              isApprovalTimeValid(selectedItem?.rawScheduleDateTime) && (
                <Button
                  variant="contained"
                  onClick={() => {
                    handlePreviewClose();
                    handleActionOpen("approve");
                  }}
                  startIcon={<ApproveIcon />}
                  sx={{
                    bgcolor: "#059669",
                    "&:hover": { bgcolor: "#047857" },
                    borderRadius: 2,
                  }}
                >
                  Approve
                </Button>
              )}
            {/* UPDATED: Show decline button even if time expired */}
            {userPermissions.write && selectedItem?.status === "Pending" && (
              <Button
                variant="outlined"
                onClick={() => {
                  handlePreviewClose();
                  handleActionOpen("decline");
                }}
                startIcon={<DeclineIcon />}
                sx={{
                  color: "#dc2626",
                  borderColor: "#dc2626",
                  "&:hover": {
                    bgcolor: alpha("#dc2626", 0.1),
                    borderColor: "#dc2626",
                  },
                  borderRadius: 2,
                }}
              >
                Decline
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Action Dialog */}
        <Dialog
          open={actionDialog}
          onClose={handleActionClose}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{ "& .MuiDialog-paper": { borderRadius: isMobile ? 0 : 2 } }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {actionType === "approve" ? (
              <ApproveIcon sx={{ color: "#059669" }} />
            ) : (
              <DeclineIcon sx={{ color: "#dc2626" }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {actionType === "approve" ? "Approve" : "Decline"} Request
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to {actionType} this request?
            </Typography>
            {selectedItem && (
              <Alert
                severity={actionType === "approve" ? "success" : "warning"}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body2">
                  <strong>Content:</strong>{" "}
                  {(
                    selectedItem.content ||
                    selectedItem.message ||
                    selectedItem.subject ||
                    ""
                  ).substring(0, 100)}
                  ...
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handleActionClose}
              disabled={isSubmitting}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={actionType === "approve" ? handleApprove : handleDecline}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : actionType === "approve" ? (
                  <ApproveIcon />
                ) : (
                  <DeclineIcon />
                )
              }
              sx={{
                bgcolor: actionType === "approve" ? "#059669" : "#dc2626",
                "&:hover": {
                  bgcolor: actionType === "approve" ? "#047857" : "#b91c1c",
                },
                borderRadius: 2,
              }}
            >
              {isSubmitting
                ? "Processing..."
                : actionType === "approve"
                ? "Approve"
                : "Decline"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Approvals;
